import { ChannelType } from '@contact/upgrade';

import { BasketChannelModel, BasketId, BasketModel } from './basket.model';
import { BasketCategoryType, BasketResult } from './models/basket';

export enum ActionTypes {
  ADD_CHANNEL = '[Basket] add channel',
  DELETE_CHANNEL = '[Basket] Delete channel',
  OPEN_BASKET = '[basket] open basket',
  CLOSE_BASKET = '[basket] close basket',
  CHANGE_CHANNEL = '[Basket] change channel',
  CHANGE_CATEGORY = '[Basket] change basket category',
  CHANGE_LABEL = '[Basket] change basket label',
  REFRESH = '[Basket] refresh',
  LOAD = '[Basket] load',
  SUCCESS_LOAD = '[Basket] load success',
  FAILURE_LOAD = '[Basket] load failure',
  OPEN_REQUEST = '[Basket] open request',
  SUCCESS_OPEN_REQUEST = '[Basket] open request success',
  FAILURE_OPEN_REQUEST = '[Basket] open request failure',
  UPDATE_REQUEST = '[Basket] update request',
  SELECT_ALL = '[Basket] select all',
  UNSELECT_ALL = '[Basket] unselect all',
  SELECT = '[Basket] select',
  UNSELECT = '[Basket] unselect',
  CHANGE_PAGE = '[Basket] change page',
  SET_PAGE_SIZE = '[Basket] set page size',
  SET_REQUEST_ERROR = '[Basket] set error requests',
  RESET_REQUEST_ERROR = '[Basket] reset error requests',
  RESET_CACHE = '[Basket] reset cache',
}

export class AddChannel<T extends BasketChannelModel = BasketChannelModel> {
  readonly type = ActionTypes.ADD_CHANNEL;

  constructor(public readonly channel: T) {}
}

export class DeleteChannel {
  readonly type = ActionTypes.DELETE_CHANNEL;

  constructor(public readonly channelType: ChannelType) {}
}

export class ChangeChannel {
  readonly type = ActionTypes.CHANGE_CHANNEL;

  constructor(public readonly channelType: ChannelType) {}
}

export class OpenBasket {
  readonly type = ActionTypes.OPEN_BASKET;
}

export class CloseBasket {
  readonly type = ActionTypes.CLOSE_BASKET;
}

export class ChangeCategory {
  readonly type = ActionTypes.CHANGE_CATEGORY;

  constructor(public readonly category: BasketCategoryType) {}
}

export class ChangeLabel {
  readonly type = ActionTypes.CHANGE_LABEL;

  constructor(public readonly label: string) {}
}

export abstract class Refresh {
  readonly type = ActionTypes.REFRESH;

  constructor(public readonly refreshType: 'auto' | 'manual' | 'navigation', public readonly sortKey?: string) {}
}

export class AutoRefresh extends Refresh {
  constructor() {
    super('auto');
  }
}

export class ManualRefresh extends Refresh {
  constructor(public override readonly sortKey?: string) {
    super('manual', sortKey);
  }
}

export class NavigationRefresh extends Refresh {
  constructor() {
    super('navigation');
  }
}

export class Load {
  readonly type = ActionTypes.LOAD;
  readonly dispatch = false;

  constructor(public readonly channel: ChannelType, public readonly label: string, public readonly autoRefresh: boolean) {}
}

export class SuccessLoad<T extends BasketModel = BasketModel> {
  readonly type = ActionTypes.SUCCESS_LOAD;

  constructor(public readonly channel: ChannelType, public readonly label: string, public readonly result: BasketResult<T>) {}
}

export class FailureLoad {
  readonly type = ActionTypes.FAILURE_LOAD;

  constructor(public readonly channel: ChannelType, public readonly label: string, public readonly err: string) {}
}

export class OpenRequest<T extends BasketModel = BasketModel> {
  readonly type = ActionTypes.OPEN_REQUEST;

  constructor(public readonly request: T) {}
}

export class SuccessOpenRequest {
  readonly type = ActionTypes.SUCCESS_OPEN_REQUEST;
}

export class FailureOpenRequest {
  readonly type = ActionTypes.FAILURE_OPEN_REQUEST;

  constructor(public readonly err: string) {}
}

export class UpdateRequest {
  readonly type = ActionTypes.UPDATE_REQUEST;

  constructor(public readonly requestId: string, public readonly change: Partial<BasketModel>) {}
}

export class SelectAll {
  readonly type = ActionTypes.SELECT_ALL;

  constructor(public readonly ids?: BasketId[]) {}
}

export class UnselectAll {
  readonly type = ActionTypes.UNSELECT_ALL;

  constructor(public readonly ids?: BasketId[]) {}
}

export class Select {
  readonly type = ActionTypes.SELECT;

  constructor(public readonly id: BasketId) {}
}

export class Unselect {
  readonly type = ActionTypes.UNSELECT;

  constructor(public readonly id: BasketId) {}
}

export class ChangePage {
  readonly type = ActionTypes.CHANGE_PAGE;

  constructor(public readonly page: number) {}
}

export class SetPageSize {
  readonly type = ActionTypes.SET_PAGE_SIZE;

  constructor(public readonly channel: ChannelType, public readonly pageSize: number) {}
}

export class SetError {
  readonly type = ActionTypes.SET_REQUEST_ERROR;

  constructor(public readonly ids: BasketId[]) {}
}

export class ResetError {
  readonly type = ActionTypes.RESET_REQUEST_ERROR;
}

export class ResetCache {
  readonly type = ActionTypes.RESET_CACHE;
}

export type ActionsUnion =
  | AddChannel
  | DeleteChannel
  | OpenBasket
  | CloseBasket
  | ChangeCategory
  | ChangeChannel
  | ChangeLabel
  | AutoRefresh
  | ManualRefresh
  | NavigationRefresh
  | Load
  | SuccessLoad
  | FailureLoad
  | OpenRequest
  | SuccessOpenRequest
  | FailureOpenRequest
  | UpdateRequest
  | SelectAll
  | UnselectAll
  | Select
  | Unselect
  | ChangePage
  | SetPageSize
  | SetError
  | ResetError
  | ResetCache;
